<template>
  <b-container>
    <b-row>
      <b-col cols-sm="12" cols-md="6" class="container-row">
        <div class="authentication-card" :class="isMobile ? ' mobile-card' : ''">
          <div class="center-content">
            <h3>
              Log in
            </h3>
          </div>
          <b-form-group
            label="Username*"
            label-for="login-username"
            @keyup.enter="onLogin()">
            <b-form-input
              id="login-username"
              v-model="loginRequest.Username"
              placeholder="user@email.com"
              :state="isValidInput(loginRequest.Username)"
              @keyup.enter="onLogin()"
              data-cy="login-user"></b-form-input>
            <b-form-invalid-feedback>Please enter a username</b-form-invalid-feedback>
          </b-form-group>
          <b-form-group
            label="Password*"
            label-for="login-password">
            <b-form-input type="password"
              id="login-password"
              v-model="loginRequest.Password"
              placeholder="******"
              :state="isValidInput(loginRequest.Password)"
              @keyup.enter="onLogin()"
              data-cy="login-password"></b-form-input>
            <b-form-invalid-feedback>Please enter a password</b-form-invalid-feedback>
          </b-form-group>
          <div>
            <b-button
              @click="onLogin()"
              variant="primary"
              class="full-width"
              :disabled="isLoading"
              data-cy="login-button">
              <template v-if="isLoading">
                <loading loadingText="Submitting"></loading>
              </template>
              <template v-else>
                Log in
              </template>
            </b-button>
          </div>
          <div class="mt-2">
            <a href="/authentication/forgot-password">Did you forget your password?</a>
          </div>
          <div class="mt-2">
            <span>Do you need an account? <a href="/authentication/signup">Create one</a></span>
          </div>
        </div>
      </b-col>
    </b-row>
    <template v-if="acceptanceUser.Username">
      <b-modal
        v-model="displayAcceptanceModal"
        title="TPI Corporation Privacy Policy - Terms and Conditions"
        size="md"
        centered no-close-on-backdrop
        ok-title="Accept"
        cancel-title="Decline"
        @ok="acceptanceDone"
        @cancel="acceptanceDone"
        header-class="background-color-primary"
        title-class="text-white"
        header-text-variant="light"
      >
        <p>Please click on the each of the links below to review the privacy policy and terms and conditions.  Once you have accessed and reviewed the privacy policy and terms and condition the accept / decline buttons will be available.</p>
        <div v-for="doc in acceptanceDocuments" :key="doc.SiteDocumentId" class="text-center">
          <b-button variant="link" @click="selectSiteDocument(doc)">
            <b-icon icon="check-circle" class="text-success" v-if="doc.Read"></b-icon>
            <b-icon icon="circle" v-else></b-icon>
            {{ doc.DocumentName }}
          </b-button>
        </div>
        <template #modal-footer="{ ok, cancel }">
          <b-button class="background-color-primary text-white border-0" variant="primary" @click="ok()">
            Accept
          </b-button>
          <b-button class="border-0" variant="secondary" @click="cancel()">
            Decline
          </b-button>
        </template>
      </b-modal>
      <b-modal
        v-if="selectedSiteDocument"
        v-model="displayAcceptancePdf"
        :title="selectedSiteDocument.DocumentName"
        size="xl"
        centered no-close-on-backdrop
        ok-only
        header-class="background-color-primary"
        title-class="text-white"
        header-text-variant="light"
        ok-title="Reviewed"
        @ok="acceptanceOk">
        <div style="position: relative;height: 600px;">
          <PDFViewer :docPath="'/d/' + selectedSiteDocument.DocumentUrl" @onLastPage="lastPageReached" />
        </div>
        <template #modal-footer="{ ok }">
          <b-button class="background-color-primary text-white" variant="primary" @click="ok()">
            Reviewed
          </b-button>
        </template>
      </b-modal>
    </template>
    <b-modal
      v-model="displayDeclineModal"
      title="TPI Corporation Privacy Policy - Terms and Conditions"
      size="md"
      centered no-close-on-backdrop
      ok-only
      header-class="background-color-primary"
      title-class="text-white"
      header-text-variant="light"
    >
      <p> By declining the Privacy Policy and Terms and Conditions you have been redirected to this page.  If this page has been reached in error please click on TPI (Portal Site) at the top of the screen to return to the logon page. If you have questions about the documents please contact your sale management. Acceptance of the privacy policy along with the terms and conditions are required for accessing the portal.</p>
      <template #modal-footer="{ ok }">
        <b-button class="background-color-primary text-white" variant="primary" @click="ok()">
          Ok
        </b-button>
      </template>
    </b-modal>
  </b-container>
</template>
<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator'
import MxSecurity from '@/mixins/security'
import { ILoginRequest, ISiteDocument, User } from '@/store/security/types'
import Loading from '@/components/utility/loading.vue'
import { isMobile, handleResize } from '@/common/helpers/responsive-helper'
import { TPIException } from '@/store/editor/types'
import { namespace } from 'vuex-class'
import PDFViewer from '@/components/utility/pdfViewer.vue'

const nsSecurity = namespace('security')

@Component({
  components: {
    Loading,
    PDFViewer,
  },
})

export default class extends Mixins(MxSecurity) {
  @nsSecurity.State('acceptanceUser')
  public acceptanceUser!: User

  @nsSecurity.State('loginCodeUser')
  public loginCodeUser!: User

  @nsSecurity.Action('getAcceptanceDocuments')
  public getAcceptanceDocuments: any

  @nsSecurity.Action('saveUserAcceptance')
  public saveUserAcceptance: any

  public isMobile = isMobile()
  public didSubmit = false

  public loginRequest: ILoginRequest = {
    Username: '',
    Password: '',
    Scope: '',
  }

  public isLoading = false
  public displayAcceptanceModal = false
  public displayDeclineModal = false
  public displayAcceptancePdf = false
  public documentLastPageReached = false
  public acceptanceDocuments: any = []
  public selectedSiteDocument: ISiteDocument | null = null

  public created() {
    handleResize((isMobile) => {
      this.isMobile = isMobile
    })
  }

  public mounted() {
    if (this.checkAcceptance && this.acceptanceUser.Username) {
      this.acceptanceRequired()
    }
  }

  public onLogin() {
    this.didSubmit = true
    this.isLoading = true
    const { loginRequest } = this

    if (!this.isFormValid) {
      this.isLoading = false
      return
    }

    const { $router } = this

    this.login({ loginRequest }).then((user: User) => {
      if (user.AuthProviderType === 'AD' && user.Email === '') {
        $router.push('/authentication/adsignup').catch(() => { })
      } else if (this.loginCodeUser.IsCodeToken) {
        $router.push('/authentication/login-code').catch(() => { })
      } else {
        $router.push('/').catch(() => { })
      }
    }).catch(({ Code, Message } : TPIException) => {
      if (Code === 141) { // disabled user
        const h = this.$createElement
        const message = this.$createElement('div',
          {},
          [
            'Your account has been locked due to a period of inactivity. To regain access, please contact Customer Support for assistance',
            h('br'),
            h('br'),
            h('strong', 'Contact Info'),
            h('br'),
            'customerportal@tpicorp.com',
            h('br'),
            '1-800-682-3398',
            h('br'),
            h('br'),
            h('strong', 'Business Hours'),
            h('br'),
            'Monday - Friday',
            h('br'),
            '8:00 am - 5:00 pm EST',
          ])
        this.makeToastNode('Account Disabled', message, 'default', false)
      } else {
        this.toastError(Message)
      }
    }).finally(() => {
      this.isLoading = false
      if (this.resetUsername === loginRequest.Username) {
        $router.push('/authentication/reset-password')
      }
    })
  }

  public isValidInput(value: string): boolean | null {
    return !this.didSubmit
      ? null
      : (value ?? '').length > 0
  }

  private get isFormValid(): boolean {
    return (this.isValidInput(this.loginRequest.Username) ?? false) &&
      (this.isValidInput(this.loginRequest.Password) ?? false)
  }

  @Watch('acceptanceUser.Username')
  public acceptanceRequired() {
    if (this.acceptanceUser.Username) {
      this.getAcceptanceDocuments().then((docs: any) => {
        this.acceptanceDocuments = docs
        this.displayAcceptanceModal = true
      })
    }
  }

  public lastPageReached() {
    this.selectedSiteDocument!.Read = true
    this.documentLastPageReached = true
  }

  public selectSiteDocument(document: any) {
    this.selectedSiteDocument = document
    this.documentLastPageReached = false
    this.displayAcceptancePdf = true
  }

  public acceptanceOk(e: any) {
    if (!this.selectedSiteDocument!.Read) {
      this.toastError('You must read through to the end')
      e.preventDefault()
    }
  }

  public acceptanceDone(e: any) {
    const { $router } = this
    const accepted = e.trigger === 'ok'
    if (!accepted && this.acceptanceDocuments.filter(s => !s.Read).length === 0) {
      this.displayDeclineModal = true
    }
    if (this.acceptanceDocuments.filter(s => !s.Read).length > 0) {
      this.toastError('You must access and review each of the documents prior to clicking on the accept or decline button.')
      e.preventDefault()
    } else {
      this.saveUserAcceptance({
        siteDocumentIds: this.acceptanceDocuments.map((s: ISiteDocument) => s.SiteDocumentId),
        accepted,
      }).then((result) => {
        if (result) {
          $router.push('/').catch(() => { })
        }
      })
    }
  }

  public get checkAcceptance() {
    return this.$route.params.checkAcceptance
  }
}
</script>
<style>
@import '../../styles/authentication.css'
</style>
